// src/pages/ContactPage.tsx
import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  FiSend,
  FiUser,
  FiMail,
  FiMessageSquare,
  FiCheckCircle,
  FiPhone,
  FiMapPin,
  FiClock,
  FiInstagram,
  FiAlertTriangle,
  FiMap,
  FiNavigation,
} from 'react-icons/fi';
import { FaFacebook, FaWhatsapp, FaTripadvisor } from 'react-icons/fa';
import Seo from '../components/Seo';

type ApiError = { msg: string; param: string };

const ContactPage: React.FC = () => {
  const API_BASE = import.meta.env.VITE_API_URL || '/.netlify/functions/api';

  // Map embed via deterministic lat/lng (no PB, no API key)
  const EMBED_COORDS = { lat: 29.528769, lng: 35.000269 };

  // Public-facing details (display only)
  const contactInfo = {
    email: 'aboveandbelowadventures@gmail.com',
    phone: '+962 79 723 7623',
    address: 'The Royal Yacht Club of Jordan, Aqaba 77110',
    hours: 'Mon - Sun: 9:00 AM – 8:00 PM',
    website: 'www.aboveandbelowadventuresjo.com',
    // Match the embed location so buttons open the same spot
    coordinates: EMBED_COORDS,
  };

  // Social links (edit these)
  const socials = {
    instagram:
      'https://www.instagram.com/above_and_below_adventures/?igsh=MW9keDg1enE3amxtbg%3D%3D#',
    facebook: 'https://www.facebook.com/aboveandbelow.info/',
    tripadvisor:
      'https://www.tripadvisor.com/Attraction_Review-g298101-d7368530-Reviews-Above_and_Below_Adventures_Day_Tours-Aqaba_Al_Aqabah_Governorate.html',
    whatsappPhone: '+962 79 723 7623', // must include country code
    whatsappMessage: 'Hello! I’d like to plan a trip with Above & Below Adventures.',
  };
  const whatsappDigits = socials.whatsappPhone.replaceAll(/\D/g, '');
  const whatsappLink = `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(
    socials.whatsappMessage
  )}`;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const mapsSrc = `https://www.google.com/maps?q=${EMBED_COORDS.lat},${EMBED_COORDS.lng}&z=18&hl=en&output=embed&t=k&v=2`;

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email))
      newErrors.email = 'Please enter a valid email';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 10)
      newErrors.message = 'Message should be at least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
    if (serverError) setServerError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!validateForm()) return;

    setIsSubmitting(true);
    setIsSuccess(false);

    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        // API might return { errors: [{ msg, param }, ...] } or { message: '...' }
        if (data?.errors?.length) {
          const fieldErrors: Record<string, string> = {};
          for (const e of data.errors as ApiError[]) {
            if (e.param) fieldErrors[e.param] = e.msg;
          }
          if (Object.keys(fieldErrors).length) setErrors(fieldErrors);
        }
        setServerError(
          data?.message || 'Failed to send message. Please try again.'
        );
        setIsSuccess(false);
        setIsSubmitting(false);
        return;
      }

      // Success
      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Auto-hide success after a few seconds
      setTimeout(() => setIsSuccess(false), 6000);
    } catch {
      setServerError(
        'Network error. Please check your connection and try again.'
      );
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Buttons (Open in Maps / Directions)
  const openUrl = contactInfo.coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${contactInfo.coordinates.lat},${contactInfo.coordinates.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`;

  const directionsUrl = contactInfo.coordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${contactInfo.coordinates.lat},${contactInfo.coordinates.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(contactInfo.address)}`;

  return (
    <div className="min-h-screen text-slate-100 relative overflow-hidden bg-slate-950">
      <Header />
      <Seo
        title="Contact Above & Below Adventures — Aqaba, Jordan"
        description="Questions or custom itineraries? Contact Above & Below Adventures in Aqaba. Phone +962 79 723 7623 • Email aboveandbelowadventures@gmail.com • Open daily 9:00–20:00."
        canonicalPath="/contact"
        image="/assets/logo.png"
        jsonLd={{
          '@type': 'TravelAgency',
          name: 'Above & Below Adventures',
          url: import.meta.env.VITE_SITE_URL,
          telephone: '+962 79 723 7623',
          email: 'aboveandbelowadventures@gmail.com',
          image: `${import.meta.env.VITE_SITE_URL}/assets/logo.png`,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'The Royal Yacht Club of Jordan',
            addressLocality: 'Aqaba',
            postalCode: '77110',
            addressCountry: 'JO',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 29.528769,
            longitude: 35.000269,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
              ],
              opens: '09:00',
              closes: '20:00',
            },
          ],
          areaServed: 'Jordan',
          sameAs: [
            'https://www.facebook.com/aboveandbelow.info/',
            'https://www.tripadvisor.com/Attraction_Review-g298101-d7368530-Reviews-Above_and_Below_Adventures_Day_Tours-Aqaba_Al_Aqabah_Governorate.html',
            'https://www.instagram.com/above_and_below_adventures/?igsh=MW9keDg1enE3amxtbg%3D%3D#',
          ],
          hasMap: `https://www.google.com/maps?q=29.528769,35.000269&z=18&hl=en&output=embed&t=k&v=2`,
        }}
      />

      <main className="relative">

        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30">
          <div className="mx-3 mb-3 rounded-2xl shadow-xl border border-white/10 overflow-hidden backdrop-blur supports-[backdrop-filter]:bg-slate-900/70 bg-slate-900/80">
            <div className="grid grid-cols-3 divide-x divide-white/10">
              <a
                href={`tel:${contactInfo.phone.replaceAll(/\s/g, '')}`}
                className="flex items-center justify-center gap-2 py-3 text-white text-sm active:opacity-80"
                aria-label="Call us"
              >
                <FiPhone />
                <span>Call</span>
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-3 text-white text-sm active:opacity-80"
                aria-label="Chat on WhatsApp"
              >
                <FaWhatsapp />
                <span>WhatsApp</span>
              </a>
              <a
                href={directionsUrl}
                className="flex items-center justify-center gap-2 py-3 text-white text-sm active:opacity-80"
                aria-label="Get directions"
              >
                <FiMapPin />
                <span>Go</span>
              </a>
            </div>
          </div>
        </nav>

        <section className="relative px-4 pt-14 pb-24 sm:pt-16">
          <div aria-live="polite" className="sr-only">
            {isSuccess ? 'Message sent successfully!' : serverError || ''}
          </div>

          <div className="max-w-6xl mx-auto relative z-10 space-y-12">
            <div className="space-y-10">
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-white">
                    Contact Us
                  </h1>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <a
                    href={socials.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-center gap-3 transition-all hover:border-white/30 hover:-translate-y-0.5"
                  >
                    <span className="text-blue-200">
                      <FaFacebook />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Facebook</p>
                      <p className="text-sm font-semibold text-white">Community updates</p>
                    </div>
                  </a>
                  <a
                    href={socials.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-center gap-3 transition-all hover:border-white/30 hover:-translate-y-0.5"
                  >
                    <span className="text-pink-200">
                      <FiInstagram />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Instagram</p>
                      <p className="text-sm font-semibold text-white">Daily visuals</p>
                    </div>
                  </a>
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-center gap-3 transition-all hover:border-white/30 hover:-translate-y-0.5"
                  >
                    <span className="text-emerald-200">
                      <FaWhatsapp />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">WhatsApp</p>
                      <p className="text-sm font-semibold text-white">Fastest replies</p>
                    </div>
                  </a>
                  <a
                    href={socials.tripadvisor}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-center gap-3 transition-all hover:border-white/30 hover:-translate-y-0.5"
                  >
                    <span className="text-amber-200">
                      <FaTripadvisor />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Tripadvisor</p>
                      <p className="text-sm font-semibold text-white">Guest reviews</p>
                    </div>
                  </a>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3 py-1">
                    <div className="mt-0.5 text-cyan-300">
                      <FiMail />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</p>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-lg font-semibold text-white hover:text-cyan-300 transition-colors break-words"
                        title={contactInfo.email}
                      >
                        {contactInfo.email}
                      </a>
                      <a
                        href="mailto:aboveandbelow2014@outlook.com"
                        className="mt-1 block text-lg font-semibold text-white hover:text-cyan-300 transition-colors break-words"
                        title="aboveandbelow2014@outlook.com"
                      >
                        aboveandbelow2014@outlook.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-1">
                    <div className="mt-0.5 text-cyan-300">
                      <FiPhone />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Phone</p>
                      <a
                        href={`tel:${contactInfo.phone.replaceAll(/\s/g, '')}`}
                        className="text-lg font-semibold text-white hover:text-cyan-300 transition-colors break-words"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-1">
                    <div className="mt-0.5 text-cyan-300">
                      <FiMapPin />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Address</p>
                      <p className="text-lg font-semibold text-white">{contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-1">
                    <div className="mt-0.5 text-cyan-300">
                      <FiClock />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Hours</p>
                      <p className="text-lg font-semibold text-white">{contactInfo.hours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl">
                <div className="relative border-y border-white/10 bg-black/50">
                  <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 via-transparent to-emerald-500/10"></div>
                  <div className="relative rounded-2xl overflow-hidden m-4 border border-white/5 aspect-[4/3] sm:aspect-[16/10]">
                    <iframe
                      src={mapsSrc}
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Above & Below Adventures Map"
                    ></iframe>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 p-5 sm:p-6">
                  <a
                    href={openUrl}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-cyan-400/70 hover:bg-white/10"
                  >
                    <FiMap className="text-cyan-300" />
                    <span>Open in Google Maps</span>
                  </a>
                  <a
                    href={directionsUrl}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-emerald-400/70 hover:bg-white/10"
                  >
                    <FiNavigation className="text-emerald-300" />
                    <span>Get Directions</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(94,234,212,0.08),transparent_30%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.08),transparent_25%)]"></div>
              <div className="relative flex flex-wrap items-start justify-between gap-4 px-5 sm:px-7 py-6 border-b border-white/10">
                <div className="space-y-2 max-w-2xl">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Message</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white">
                    Tell us about your dive, cruise, or desert plan
                  </h3>
                  <p className="text-slate-300 text-sm sm:text-base">
                    Include dates, group size, and goals. We’ll confirm availability and shape a clear itinerary with one
                    follow-up.
                  </p>
                </div>
                <div className="flex flex-col gap-2 min-w-[220px] max-w-xs">
                  {isSuccess && (
                    <output className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-emerald-100">
                      <FiCheckCircle />
                      <span>Message sent. We’ll reply shortly.</span>
                    </output>
                  )}
                  {serverError && (
                    <output className="inline-flex items-center gap-2 rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-rose-100">
                      <FiAlertTriangle />
                      <span>{serverError}</span>
                    </output>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting} className="relative p-5 sm:p-7 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-slate-300 mb-1 font-medium text-sm sm:text-base"
                    >
                      Name
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                        <FiUser />
                      </span>
                      <input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        autoComplete="name"
                        inputMode="text"
                        className={`w-full text-base pl-10 pr-3 py-3 rounded-xl bg-white/5 backdrop-blur border ${
                          errors.name ? 'border-rose-500/60' : 'border-white/10'
                        } focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-white placeholder-slate-400 caret-cyan-400 selection:bg-cyan-500/20`}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                        style={{ WebkitTextFillColor: '#ffffff' }}
                      />
                    </div>
                    {errors.name && (
                      <p id="name-error" className="mt-1 text-sm text-rose-200">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-slate-300 mb-1 font-medium text-sm sm:text-base"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                        <FiMail />
                      </span>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        autoComplete="email"
                        inputMode="email"
                        className={`w-full text-base pl-10 pr-3 py-3 rounded-xl bg-white/5 backdrop-blur border ${
                          errors.email ? 'border-rose-500/60' : 'border-white/10'
                        } focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-white placeholder-slate-400 caret-cyan-400 selection:bg-cyan-500/20`}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        style={{ WebkitTextFillColor: '#ffffff' }}
                      />
                    </div>
                    {errors.email && (
                      <p id="email-error" className="mt-1 text-sm text-rose-200">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="subject"
                      className="block text-slate-300 mb-1 font-medium text-sm sm:text-base"
                    >
                      Subject
                    </label>
                    <input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Trip type, dates, or topic"
                      autoComplete="off"
                      inputMode="text"
                      className={`w-full text-base px-3 py-3 rounded-xl bg-white/5 backdrop-blur border ${
                        errors.subject ? 'border-rose-500/60' : 'border-white/10'
                      } focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-white placeholder-slate-400 caret-cyan-400 selection:bg-cyan-500/20`}
                      aria-invalid={!!errors.subject}
                      aria-describedby={errors.subject ? 'subject-error' : undefined}
                      style={{ WebkitTextFillColor: '#ffffff' }}
                    />
                    {errors.subject && (
                      <p id="subject-error" className="mt-1 text-sm text-rose-200">
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="message"
                      className="block text-slate-300 mb-1 font-medium text-sm sm:text-base"
                    >
                      Message
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-3 text-slate-500">
                        <FiMessageSquare />
                      </span>
                      <textarea
                        id="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        placeholder="Tell us about your trip, ideas, or any questions you have..."
                        className={`w-full text-base pl-10 pr-3 py-3 rounded-xl bg-white/5 backdrop-blur border ${
                          errors.message ? 'border-rose-500/60' : 'border-white/10'
                        } focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-white placeholder-slate-400 caret-cyan-400 selection:bg-cyan-500/20 resize-y`}
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? 'message-error' : undefined}
                        style={{ WebkitTextFillColor: '#ffffff' }}
                      />
                    </div>
                    {errors.message && (
                      <p id="message-error" className="mt-1 text-sm text-rose-200">
                        {errors.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-cyan-400/70 hover:bg-white/10 disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-label={isSubmitting ? 'Sending message' : 'Send message'}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FiSend aria-hidden="true" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>

                  {isSuccess && (
                    <span className="text-emerald-200 inline-flex items-center gap-1 text-sm font-semibold">
                      <FiCheckCircle /> Sent
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
